import React, { useState } from 'react';
import { X, AlertTriangle, Pause, Play } from 'lucide-react';

interface SuspensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  currentStatus: string;
  onSuspend: (tenantId: string, reason: string) => Promise<void>;
  onReactivate: (tenantId: string) => Promise<void>;
}

const suspensionReasons = [
  'Non-payment',
  'Terms of service violation',
  'Security concerns',
  'Excessive resource usage',
  'Customer request',
  'Maintenance',
  'Legal compliance',
  'Other'
];

export const SuspensionModal: React.FC<SuspensionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  currentStatus,
  onSuspend,
  onReactivate
}) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);

  const isSuspended = currentStatus === 'SUSPENDED';
  const action = isSuspended ? 'reactivate' : 'suspend';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (isSuspended) {
        await onReactivate(tenantId);
      } else {
        const finalReason = reason === 'Other' ? customReason : reason;
        if (!finalReason.trim()) {
          alert('Please provide a reason for suspension');
          return;
        }
        await onSuspend(tenantId, finalReason);
      }
      
      onClose();
    } catch (error) {
      console.error(`Failed to ${action} firm:`, error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setReason('');
    setCustomReason('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            {isSuspended ? (
              <Play className="w-6 h-6 text-green-500 mr-3" />
            ) : (
              <Pause className="w-6 h-6 text-yellow-500 mr-3" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isSuspended ? 'Reactivate' : 'Suspend'} Firm
              </h2>
              <p className="text-gray-600 text-sm mt-1">{tenantName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {isSuspended ? (
              // Reactivation confirmation
              <div>
                <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <Play className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <h3 className="font-medium text-green-800">Reactivate Firm</h3>
                    <p className="text-sm text-green-700 mt-1">
                      This will restore full access to the firm's account and services.
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm">
                  Are you sure you want to reactivate <strong>{tenantName}</strong>? 
                  The firm will regain immediate access to their account and all services.
                </p>
              </div>
            ) : (
              // Suspension form
              <div>
                <div className="flex items-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3" />
                  <div>
                    <h3 className="font-medium text-yellow-800">Suspend Firm</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      This will temporarily restrict access to the firm's account while keeping data intact.
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Suspension *
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a reason...</option>
                    {suspensionReasons.map((reasonOption) => (
                      <option key={reasonOption} value={reasonOption}>
                        {reasonOption}
                      </option>
                    ))}
                  </select>
                </div>

                {reason === 'Other' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Reason *
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Please specify the reason for suspension..."
                      required
                    />
                  </div>
                )}

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">Impact of Suspension:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• Immediate loss of access to the platform</li>
                    <li>• All user sessions will be terminated</li>
                    <li>• API access will be blocked</li>
                    <li>• Data remains intact but inaccessible</li>
                    <li>• Billing continues unless plan is changed</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isSuspended && !reason)}
              className={`btn-${isSuspended ? 'success' : 'warning'}`}
            >
              {loading ? (
                `${isSuspended ? 'Reactivating' : 'Suspending'}...`
              ) : (
                `${isSuspended ? 'Reactivate' : 'Suspend'} Firm`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
